import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  FormControl,
  Menu,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Backdrop,
  CircularProgress,
  ThemeProvider,
  Select,
  MenuItem,
  Link as MuiLink,
} from "@mui/material";
import darkTheme from "../../style/theme";
import { useNavigate, useParams, useLocation, Link as RouterLink } from "react-router-dom";
import { Icon as OpusIcon, Button as OpusButton, Field as OpusField, TextInput as OpusTextInput, Modal as OpusModal, Card as OpusCard, FieldBox, SearchInput as OpusSearchInput } from "../../components/common/opus";
import CaptionsDataGrid from "../../components/scenes/CaptionsDataGrid";
import AddCaptionDialog from "../../components/scenes/AddCaptionDialog";
import VersionDialog from "../../components/scenes/VersionDialog";
import FolderSelector from "../../components/FolderSelector";
import DashboardBreadcrumbs from "../../components/DashboardBreadcrumbs";
import { useAlert } from "../../contexts/AlertContext";
import { AuthContext } from "../../contexts/AuthContext";
import apiService from "../../services/api.service";
import S3ApiService from "../../services/s3-api.service";
import * as sceneCache from "../../utils/sceneDataCache";
import { useRegisterUnsavedChangesGuard } from "../../contexts/UnsavedChangesContext";
import { LAYERS, DEFAULT_LAYER, normalizeLayer, layerMeta, fromApiCaption, toCaptionPayload } from "../../services/scenes/captionPayload";

const SceneDetailsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sceneId, orgId } = useParams();
  const alert = useAlert();
  const { userInfo } = React.useContext(AuthContext);
  const addCaptionFileInputRef = React.useRef(null);
  // Track captions removed during editing so we can delete their S3 folders on save
  const removedCaptionsRef = React.useRef([]);
  // Newly added captions are prepended to the top of the list — scroll them
  // into view since the Captions section sits below Scene Name/Organisation/
  // folder fields and can be off-screen when a caption gets added.
  const captionsSectionRef = React.useRef(null);
  const [scrollToCaptionsTrigger, setScrollToCaptionsTrigger] = useState(0);
  // { index, nonce } — a new caption is always prepended at index 0, and the
  // nonce (changes every add) lets the grid re-focus even if index repeats.
  const [captionFocusRequest, setCaptionFocusRequest] = useState(null);

  useEffect(() => {
    if (scrollToCaptionsTrigger > 0) {
      captionsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [scrollToCaptionsTrigger]);

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  // OPUS-1045: org scenes for the breadcrumb scene-switcher dropdown.
  const [orgScenes, setOrgScenes] = useState([]);
  const [selectedLoadPartPath, setSelectedLoadPartPath] = useState(null);
  const [selectedPlayerPath, setSelectedPlayerPath] = useState(null);
  const [selectedTeamBadgePath, setSelectedTeamBadgePath] = useState(null);

  const [sceneData, setSceneData] = useState({
    _id: "",
    scene_name: "",
    organization_id: "",
    load_part_path: null,
    load_player_path: null,
    load_team_badge_path: null,
    captions: [],
  });

  const [versionMenu, setVersionMenu] = useState({
    open: false,
    anchorEl: null,
    captionIndex: -1,
    versions: [],
  });

  const [versionDialog, setVersionDialog] = useState({
    open: false,
    captionIndex: -1,
    isMajorChange: false,
    file: null,
  });
  const [addCaptionDialogOpen, setAddCaptionDialogOpen] = useState(false);
  const [confirmCreateManuallyOpen, setConfirmCreateManuallyOpen] = useState(false);
  const [captionSearch, setCaptionSearch] = useState("");
  // Tracks any local edit (graphic add/remove/change, field edit) not yet sent
  // via Update/Create — surfaced next to the button so removing a graphic
  // doesn't look identical to actually saving it.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Holds the navigation the user was trying to make (Back / breadcrumb /
  // scene switcher) while changes are unsaved, so the leave-confirmation
  // modal can carry it out after a Discard or a successful Save.
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const isEditing = Boolean(sceneId && sceneId !== "new");

  // Filter operates on a copy annotated with each caption's index in
  // sceneData.captions — CaptionsDataGrid's edit/delete handlers key off that
  // original index, so it must survive filtering instead of being recomputed
  // from position in the (possibly narrowed) filtered array.
  const filteredCaptions = React.useMemo(() => {
    const q = captionSearch.trim().toLowerCase();
    const withIndex = sceneData.captions.map((c, i) => ({ ...c, _originalIndex: i }));
    if (!q) return withIndex;
    return withIndex.filter((c) =>
      [c.caption_name, c.description, c.version].some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [sceneData.captions, captionSearch]);

  // Computed values
  const orgName = React.useMemo(() => {
    const org = organizations.find((org) => org._id === sceneData.organization_id);
    return org?.name || "";
  }, [organizations, sceneData.organization_id]);

  const scenePathName = React.useMemo(() => {
    return sceneData.scene_name || "";
  }, [sceneData.scene_name]);

  // For file paths (lowercase with hyphens)
  const orgNamePath = React.useMemo(() => {
    return orgName?.toLowerCase().replace(/\s+/g, "-") || "";
  }, [orgName]);

  const sceneNamePath = React.useMemo(() => {
    return scenePathName?.toLowerCase().replace(/\s+/g, "-") || "";
  }, [scenePathName]);

  // Load organizations (use cache if valid for instant back-navigation)
  useEffect(() => {
    const cached = sceneCache.getOrganizations();
    if (cached) setOrganizations(cached);
    apiService
      .getOrganizationsFromScene()
      .then((data) => {
        setOrganizations(data);
        sceneCache.setOrganizations(data);
      })
      .catch((error) => {
        console.error("Error fetching organizations:", error);
        if (!sceneCache.getOrganizations()) alert.error("Failed to load organizations");
      });
  }, []);

  // Load scene data if editing; use cache first for instant back-navigation
  useEffect(() => {
    setHasUnsavedChanges(false);
    if (sceneId && sceneId !== "new" && orgId) {
      const cached = sceneCache.getScenesByOrg(orgId);

      const buildSceneData = (scene) => ({
        _id: scene._id || scene.id,
        scene_name: scene.name || scene.scene_name,
        organization_id: orgId,
        load_part_path: scene.load_part_path || null,
        created_by: scene.created_by,
        // OPUS-1391: shared mapper. It also reads back `disabled` and `layer`,
        // which this map dropped — and since the save replaces the whole
        // captions array, dropping on load meant deleting on save.
        captions: (scene.captions || []).map(fromApiCaption),
      });

      // Merge API data while preserving any locally-added captions not yet saved to DB.
      const applyScene = (scene, preserveLocalAdditions = false) => {
        const data = buildSceneData(scene);
        setSelectedLoadPartPath(scene.load_part_path || null);
        if (preserveLocalAdditions) {
          setSceneData((prev) => {
            const apiIds = new Set((data.captions || []).map((c) => c.id));
            const localOnly = (prev.captions || []).filter((c) => c.id && !apiIds.has(c.id));
            return { ...data, captions: [...data.captions, ...localOnly] };
          });
        } else {
          setSceneData(data);
        }
      };

      const sceneFromData = (data) => data?.scenes?.find((s) => (s._id || s.id) === sceneId);
      if (cached?.scenes) {
        setOrgScenes(cached.scenes); // OPUS-1045: breadcrumb scene-switcher options
        const scene = sceneFromData(cached);
        if (scene) {
          applyScene(scene, false);
          setLoading(false);
        }
      }
      if (!cached?.scenes) setLoading(true);
      apiService
        .getScenesByOrganization(orgId)
        .then((data) => {
          sceneCache.setScenesByOrg(orgId, data);
          setOrgScenes(data?.scenes || []); // OPUS-1045: breadcrumb scene-switcher options
          const scene = sceneFromData(data);
          if (scene) {
            // preserveLocalAdditions=true: keep captions added in this session but not yet saved
            applyScene(scene, true);
            setSelectedPlayerPath(scene.load_player_path || null);
            setSelectedTeamBadgePath(scene.load_team_badge_path || null);
            setSceneData((prev) => ({
              ...prev,
              load_player_path: scene.load_player_path || null,
              load_team_badge_path: scene.load_team_badge_path || null,
            }));
          }
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error loading scene:", error);
          if (!sceneCache.getScenesByOrg(orgId)) alert.error("Failed to load scene details");
          setLoading(false);
        });
    } else {
      setSceneData({
        _id: "",
        scene_name: "",
        organization_id: orgId || "",
        load_part_path: null,
        captions: [],
      });
      setSelectedLoadPartPath(null);
      setLoading(false);
    }
  }, [sceneId, orgId]);

  // When returning from caption editor: merge addedCaption and preserve sceneName + existingCaptions
  useEffect(() => {
    const addedCaption = location.state?.addedCaption;
    const sceneNameFromState = location.state?.sceneName;
    const existingCaptionsFromState = location.state?.existingCaptions;
    if (addedCaption || sceneNameFromState || (existingCaptionsFromState && existingCaptionsFromState.length > 0)) {
      setSceneData((prev) => {
        let next = { ...prev };
        if (sceneNameFromState && (sceneId === "new" || !sceneId)) {
          next.scene_name = sceneNameFromState;
        }
        // Use existingCaptions from state as base (preserves captions added before navigating to editor)
        const baseCaptions = Array.isArray(existingCaptionsFromState) ? existingCaptionsFromState : prev.captions || [];
        if (addedCaption) {
          const normalized = {
            ...fromApiCaption(addedCaption),
            caption_name: addedCaption.caption_name || addedCaption.id,
            fileName: `${addedCaption.id}.zip`,
            all_captions: [],
            // OPUS-1391: the caption editor round-trips the layer it was created
            // from, so it comes back to the layer it was added on.
            layer: normalizeLayer(addedCaption.layer || location.state?.captionLayer),
          };
          const exists = baseCaptions.some((c) => (c.id || c._id) === normalized.id);
          next.captions = exists ? baseCaptions : [normalized, ...baseCaptions];
        } else if (baseCaptions.length > 0 && (prev.captions || []).length === 0) {
          next.captions = baseCaptions;
        }
        return next;
      });
      if (addedCaption) {
        setCaptionSearch("");
        setScrollToCaptionsTrigger((n) => n + 1);
        setHasUnsavedChanges(true);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.addedCaption, location.state?.sceneName, location.state?.existingCaptions, location.state?.captionLayer, location.pathname, navigate, sceneId]);

  const handleSceneChange = (event) => {
    const { name, value } = event.target;
    setSceneData((prev) => ({ ...prev, [name]: value }));
    setHasUnsavedChanges(true);
  };

  // Prefill Graphic Name from the uploaded file's name (sans extension) — the
  // field stays focused/selected right after so it's still trivial to edit.
  const stripExtension = (filename) => filename.replace(/\.[^/.]+$/, "");

  const addCaptionRow = (file = null, layer = DEFAULT_LAYER) => {
    const caption_name = file ? stripExtension(file.name) : "";
    return {
      id: caption_name.replace(/\s+/g, "_"),
      caption_name,
      description: "",
      version: "v1.0",
      existingFile: false,
      file,
      all_versions: [],
      all_captions: [],
      // OPUS-1391: lands on the layer picked in the Add dialog.
      layer: normalizeLayer(layer),
    };
  };

  // OPUS-1391: the dialog owns the step + chosen layer (AddCaptionDialog) and
  // hands the layer to whichever route the operator takes. pendingLayer only
  // needs to survive the hop to the file picker and the caption editor.
  const [pendingLayer, setPendingLayer] = useState(DEFAULT_LAYER);

  const handleAddCaption = () => setAddCaptionDialogOpen(true);

  const handleAddCaptionManually = (layer) => {
    const chosen = normalizeLayer(layer);
    setPendingLayer(chosen);
    setAddCaptionDialogOpen(false);
    if (isEditing) {
      navigate(`/dashboard/scene-management/${orgId}/${sceneId}/captions/new`, {
        state: {
          sceneName: sceneData.scene_name,
          existingCaptions: sceneData.captions || [],
          // Carried through the caption editor and back, so a caption created
          // on Front returns to Front.
          captionLayer: chosen,
        },
      });
    } else {
      setConfirmCreateManuallyOpen(true);
    }
  };

  const handleConfirmCreateManually = () => {
    setConfirmCreateManuallyOpen(false);
    navigate(`/dashboard/scene-management/${orgId}/${sceneId || "new"}/captions/new`, {
      state: {
        sceneName: sceneData.scene_name,
        existingCaptions: sceneData.captions || [],
        captionLayer: pendingLayer,
      },
    });
  };

  const handleAddCaptionUploadClick = (layer) => {
    setPendingLayer(normalizeLayer(layer));
    setAddCaptionDialogOpen(false);
    addCaptionFileInputRef.current?.click();
  };

  const handleAddCaptionFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSceneData((prev) => ({
        ...prev,
        captions: [addCaptionRow(file, pendingLayer), ...prev.captions],
      }));
      setCaptionSearch("");
      setScrollToCaptionsTrigger((n) => n + 1);
      setCaptionFocusRequest({ index: 0, nonce: Date.now() });
      setHasUnsavedChanges(true);
    }
    e.target.value = "";
  };

  const handleCaptionChange = (index, field, value) => {
    const updatedCaptions = [...sceneData.captions];
    updatedCaptions[index][field] = value;
    if (field === "caption_name") {
      updatedCaptions[index].id = `${value.replace(/\s+/g, "_")}`;
    }
    setSceneData((prev) => ({ ...prev, captions: updatedCaptions }));
    setHasUnsavedChanges(true);
  };

  const handleFileChange = (index, file) => {
    if (isEditing && sceneData.captions[index].existingFile) {
      setVersionDialog({
        open: true,
        captionIndex: index,
        isMajorChange: false,
        file: file,
      });
    } else {
      const updatedCaptions = [...sceneData.captions];
      updatedCaptions[index].file = file;
      setSceneData((prev) => ({ ...prev, captions: updatedCaptions }));
      setHasUnsavedChanges(true);
    }
  };

  const handleVersionDialogClose = () => {
    setVersionDialog({
      open: false,
      captionIndex: -1,
      isMajorChange: false,
      file: null,
    });
  };

  const handleVersionChangeType = (isMajor) => {
    setVersionDialog((prev) => ({
      ...prev,
      isMajorChange: isMajor,
    }));
  };

  const handleVersionConfirm = () => {
    const { captionIndex, isMajorChange, file } = versionDialog;
    if (captionIndex === -1 || !file) return;

    const updatedCaptions = [...sceneData.captions];
    const caption = updatedCaptions[captionIndex];

    const prevVersion = caption.version || "v1.0";
    const versionMatch = prevVersion.match(/v(\d+)\.(\d+)/);
    if (!versionMatch) {
      alert("Could not parse version format. Expected format: v1.0");
      return;
    }

    let major = parseInt(versionMatch[1]);
    let minor = parseInt(versionMatch[2]);

    if (isMajorChange) {
      major += 1;
      minor = 0;
    } else {
      minor += 1;
    }

    const newVersion = `v${major}.${minor}`;
    const all_versions = Array.isArray(caption.all_versions)
      ? [...caption.all_versions]
      : (caption.version ? [caption.version] : []);

    if (!all_versions.includes(newVersion)) {
      all_versions.push(newVersion);
    }

    const all_captions = Array.isArray(caption.all_captions)
      ? [...caption.all_captions]
      : (caption.version ? [{ version: caption.version }] : []);

    const newVersionObj = {
      description: caption.description || "",
      version: newVersion,
      fileName: file.name,
    };

    const existingIndex = all_captions.findIndex((v) => v.version === newVersion);
    if (existingIndex >= 0) {
      all_captions[existingIndex] = newVersionObj;
    } else {
      all_captions.push(newVersionObj);
    }

    updatedCaptions[captionIndex] = {
      ...caption,
      file: file,
      version: newVersion,
      existingFile: false,
      previousVersion: caption.version,
      fileName: file.name,
      all_versions: all_versions,
      all_captions: all_captions,
    };

    setSceneData((prev) => ({ ...prev, captions: updatedCaptions }));
    setHasUnsavedChanges(true);
    handleVersionDialogClose();
  };

  // OPUS-913: enable/disable a caption (hides it from the open renderer details
  // API; greyed in the dashboard). Operates on saved captions.
  const handleToggleCaptionDisabled = async (index) => {
    const cap = sceneData.captions?.[index];
    if (!cap?.id || !sceneId || sceneId === "new") { alert.error("Save the scene before enabling/disabling captions."); return; }
    const next = !cap.disabled;
    try {
      await apiService.setCaptionDisabled(sceneData._id || sceneId, cap.id, next);
      setSceneData((prev) => ({ ...prev, captions: prev.captions.map((c, i) => (i === index ? { ...c, disabled: next } : c)) }));
      alert.success(next ? "Caption disabled" : "Caption enabled");
    } catch (e) { alert.error(e?.message || "Failed to update caption"); }
  };

  const handleRemoveCaption = (index) => {
    const removed = sceneData.captions[index];
    // Track existing captions so their S3 folders can be deleted on save
    if (removed && removed.existingFile && removed.id) {
      removedCaptionsRef.current.push({
        id: removed.id,
        caption_name: removed.caption_name || removed.id,
        version: removed.version || '',
      });
    }
    const updatedCaptions = [...sceneData.captions];
    updatedCaptions.splice(index, 1);
    setSceneData((prev) => ({ ...prev, captions: updatedCaptions }));
    setHasUnsavedChanges(true);
  };

  const handleVersionHistoryClick = async (caption, index) => {
    setVersionMenu({
      open: true,
      anchorEl: document.getElementById(`version-button-${index}`),
      captionIndex: index,
      versions: caption.all_versions || [],
    });
  };

  const handleVersionMenuClose = () => {
    setVersionMenu({
      open: false,
      anchorEl: null,
      captionIndex: -1,
      versions: [],
    });
  };

  const handleVersionSelect = (version) => {
    const { captionIndex } = versionMenu;
    if (captionIndex === -1) return;
    const updatedCaptions = [...sceneData.captions];
    updatedCaptions[captionIndex].version = version;
    setSceneData((prev) => ({
      ...prev,
      captions: updatedCaptions,
    }));
    setHasUnsavedChanges(true);
    handleVersionMenuClose();
  };

  const extractRelativePath = (folderPath) => {
    if (!folderPath) return null;
    const scenePath = `scenes/${orgNamePath}/${sceneNamePath}`;
    const normalized = folderPath.replace(/\/$/, "");
    const prefix = scenePath.replace(/\/$/, "") + "/";
    if (normalized.startsWith(prefix)) {
      return normalized.slice(prefix.length);
    }
    return normalized;
  };

  const handleEditCaptionDetail = (caption) => {
    if (!caption || !caption.id) return;
    navigate(`/dashboard/scene-management/${orgId}/${sceneId}/captions/${caption.id}`, {
      state: {
        sceneName: sceneData.scene_name,
        existingCaptions: sceneData.captions || [],
        captionLayer: pendingLayer,
      },
    });
  };

  const handleDownloadCaption = async (caption) => {
    if (!caption?.caption_name || !caption?.version) return;
    try {
      const filename = `${caption.caption_name}-${caption.version}.zip`;
      const downloadId = await S3ApiService.prepareZipDownload({
        sourceKey: `${caption.caption_name}/${caption.version}/`,
        isFolder: true,
        organizationName: orgNamePath,
        sceneName: sceneNamePath,
        filename,
      });
      const url = S3ApiService.buildZipDownloadUrl(downloadId);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error('Caption download failed:', err);
    }
  };

  const handleFolderSelect = (folder) => {
    let loadPartPath = null;
    if (folder && folder.path) {
      const pathParts = folder.path.split("/");
      const assetsIndex = pathParts.indexOf("assets");
      if (assetsIndex >= 0) {
        loadPartPath = pathParts
          .slice(assetsIndex + 1)
          .join("/")
          .replace(/\/$/, "");
      }
    }
    setSelectedLoadPartPath(loadPartPath);

    setSceneData((prev) => ({
      ...prev,
      load_part_path: loadPartPath,
    }));
    setHasUnsavedChanges(true);
  };

  const handlePlayerFolderSelect = (folder) => {
    const playerPath = folder?.path ? extractRelativePath(folder.path) : null;
    setSelectedPlayerPath(playerPath);
    setSceneData((prev) => ({
      ...prev,
      load_player_path: playerPath,
    }));
    setHasUnsavedChanges(true);
  };

  const handleTeamBadgeFolderSelect = (folder) => {
    const teamBadgePath = folder?.path ? extractRelativePath(folder.path) : null;
    setSelectedTeamBadgePath(teamBadgePath);
    setSceneData((prev) => ({
      ...prev,
      load_team_badge_path: teamBadgePath,
    }));
    setHasUnsavedChanges(true);
  };

  const handleNavigateToAssets = () => {
    // Navigate to Asset Explorer page
    navigate(`/scenes/${orgId}/${sceneId}/assets`);
  };

  const handleSave = async () => {
   
    if (!sceneData.scene_name || sceneData.captions.length === 0) {
      alert.info("Please fill in scene name and add at least one caption.");
      return false;
    }

    const organizationData = organizations.find(
      (org) => org._id === sceneData.organization_id
    );
    const organizationName = organizationData?.name?.toLowerCase();
    const sceneName = sceneData.scene_name?.toLowerCase();

    try {
      setUploading(true);
      const scenePath = `/scenes/${organizationName.toLowerCase().replace(/\s+/g, "-")}/${sceneName.toLowerCase().replace(/\s+/g, "-")}`;

      // Upload caption files
      const captionsToUpload = sceneData.captions.filter(
        (caption) => caption.file
      );

      for (let caption of captionsToUpload) {
        if (!caption.file || !caption.caption_name) continue;
        await apiService.uploadFile(caption.file, {
          organizationName: organizationName.toLowerCase().replace(/\s+/g, "-"),
          sceneName: sceneName.toLowerCase().replace(/\s+/g, "-"),
          captionName: caption.caption_name,
          version: caption.version,
        });
      }

      const userId = userInfo?.data?._id;
      const scenePayload = {
        ...(isEditing ? { _id: sceneData._id } : {}),
        scene_name: sceneData.scene_name,
        scene_path: scenePath,
        load_part_path: sceneData.load_part_path || null,
        load_player_path: sceneData.load_player_path || null,
        load_team_badge_path : sceneData.load_team_badge_path || null,
        organization_id: sceneData.organization_id,
        status: "active",
        ...(userId && {
          updated_by: userId,
          created_by: isEditing
            ? (sceneData.created_by?._id || sceneData.created_by || userId)
            : userId,
        }),
        // OPUS-1391: shared mapper — this endpoint replaces the captions array
        // wholesale, so any field missing here is deleted from the document.
        captions: sceneData.captions.map(toCaptionPayload),
      };
      if (isEditing) {
        await apiService.updateScene(sceneData._id, scenePayload);

        // Delete S3 folders for captions that were removed
        if (removedCaptionsRef.current.length > 0) {
          const orgPath = organizationName.toLowerCase().replace(/\s+/g, "-");
          const scnPath = sceneName.toLowerCase().replace(/\s+/g, "-");
          for (const removed of removedCaptionsRef.current) {
            try {
              // List all objects under the caption folder prefix
              const prefix = `scenes/${orgPath}/${scnPath}/${removed.id}/`;
              const listing = await S3ApiService.listObjects({
                prefix,
                organizationName: orgPath,
                sceneName: scnPath,
              });
              // Delete every object found under the prefix
              const objects = listing?.Contents || listing?.contents || [];
              for (const obj of objects) {
                const key = obj.Key || obj.key;
                if (key) {
                  await S3ApiService.deleteObject({
                    key,
                    organizationName: orgPath,
                    sceneName: scnPath,
                  });
                }
              }
              // Also try to delete the folder marker itself
              await S3ApiService.deleteObject({
                key: prefix,
                organizationName: orgPath,
                sceneName: scnPath,
              }).catch(() => { });
              console.log(`Deleted S3 folder for caption: ${removed.id}`);
            } catch (err) {
              console.warn(`Failed to delete S3 folder for caption ${removed.id}:`, err);
            }
          }
          removedCaptionsRef.current = [];
        }

        alert.success("Scene updated successfully");
        setHasUnsavedChanges(false);
        sceneCache.invalidateScenesByOrg(orgId);

        // Stay on this scene's edit page instead of returning to the list.
        // Clear any pending file uploads now that they've been saved, so a
        // subsequent Update doesn't try to re-upload the same file.
        setSceneData((prev) => ({
          ...prev,
          captions: prev.captions.map((caption) =>
            caption.file
              ? { ...caption, file: null, existingFile: true, previousVersion: undefined }
              : caption
          ),
        }));
      } else {
        await apiService.createScene(scenePayload);
        alert.success("Scene created successfully");
        setHasUnsavedChanges(false);
        navigate(`/dashboard/scene-management`);
      }
      return true;
    } catch (error) {
      console.error("Error saving scene:", error);
      alert.error(`Failed to ${isEditing ? "update" : "create"} scene`);
      return false;
    } finally {
      setUploading(false);
    }
  };

  // Any exit from this page (Back, breadcrumb link, scene switcher) routes
  // through here instead of navigating directly, so a dirty form always gets
  // the in-app "unsaved changes" prompt rather than leaving silently.
  const requestLeave = (leaveFn) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(() => leaveFn);
    } else {
      leaveFn();
    }
  };

  const handleCancel = () => {
    requestLeave(() => navigate(`/dashboard/scene-management`));
  };

  const handleDiscardAndLeave = () => {
    const leaveFn = pendingNavigation;
    setHasUnsavedChanges(false);
    setPendingNavigation(null);
    leaveFn && leaveFn();
  };

  const handleSaveAndLeave = async () => {
    const saved = await handleSave();
    if (saved) {
      const leaveFn = pendingNavigation;
      setPendingNavigation(null);
      leaveFn && leaveFn();
    }
  };

  // Lets the sidebar (a component outside this page) reuse this same
  // requestLeave/modal when the user clicks a nav item while this page is dirty.
  useRegisterUnsavedChangesGuard(hasUnsavedChanges, requestLeave);

  // The physical browser Back/Forward button doesn't go through React Router
  // at all — it fires a native popstate after the URL has already changed, so
  // there's nothing to "cancel" through the router. This traps it with the
  // standard workaround: push a duplicate history entry so the first Back
  // press lands on it (harmlessly re-showing the same URL) instead of actually
  // leaving, which gives us a chance to show the same confirmation modal
  // before letting a confirmed Back press go through for real.
  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    window.history.pushState(null, "", window.location.href);
    const onPopState = () => {
      window.history.pushState(null, "", window.location.href);
      requestLeave(() => {
        window.removeEventListener("popstate", onPopState);
        window.history.go(-2);
      });
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnsavedChanges]);

  if (loading) {
    return (
      <ThemeProvider theme={darkTheme}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
          }}
        >
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <Grid container spacing={1} sx={{ height: "100vh" }}>
        {/* Header */}
        <Grid size={12}>
          <DashboardBreadcrumbs
            breadcrumbContent={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <MuiLink
                  component={RouterLink}
                  to="/dashboard/scene-management"
                  underline="hover"
                  onClick={(e) => {
                    if (hasUnsavedChanges) {
                      e.preventDefault();
                      requestLeave(() => navigate("/dashboard/scene-management"));
                    }
                  }}
                  sx={{ color: "text.secondary", fontSize: "1.25rem", lineHeight: 1.2, fontWeight: 400 }}
                >
                  Scene Management
                </MuiLink>
                <Box component="span" sx={{ color: "var(--text-dim)", fontSize: "1.25rem" }}>/</Box>
                {/* OPUS-1045: switch scenes from the breadcrumb without going back to the list. */}
                {isEditing && orgScenes.length > 0 ? (
                  <Select
                    value={sceneId}
                    onChange={(e) => {
                      const targetSceneId = e.target.value;
                      requestLeave(() => navigate(`/dashboard/scene-management/${orgId}/${targetSceneId}/edit`));
                    }}
                    variant="standard"
                    disableUnderline
                    sx={{
                      color: "text.primary", fontSize: "1.25rem", fontWeight: 600, lineHeight: 1.2,
                      "& .MuiSelect-select": { py: 0, pr: "20px !important" },
                      "& .MuiSvgIcon-root": { color: "var(--text-dim)" },
                    }}
                  >
                    {orgScenes.map((s) => (
                      <MenuItem key={s._id || s.id} value={s._id || s.id}>{s.name || s.scene_name}</MenuItem>
                    ))}
                  </Select>
                ) : (
                  <Typography component="span" sx={{ color: "text.primary", fontSize: "1.25rem", lineHeight: 1.2, fontWeight: 600 }}>
                    {isEditing ? (scenePathName || "Scene") : "New Scene"}
                  </Typography>
                )}
              </Box>
            }
            rightAction={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                {hasUnsavedChanges && !uploading && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, color: "var(--amber)", fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap" }}>
                    <OpusIcon name="alertTriangle" size={14} />
                    Unsaved changes
                  </Box>
                )}
                <OpusButton variant="secondary" size="sm" icon="chevronLeft" onClick={handleCancel}>Back</OpusButton>
                <OpusButton variant="primary" size="sm" icon="check" onClick={handleSave} disabled={uploading}>
                  {uploading ? (isEditing ? "Updating…" : "Creating…") : (isEditing ? "Update" : "Create")}
                </OpusButton>
              </Box>
            }
          />
        </Grid>

        {/* Content — canonical surface-1 panel (matches Scene Management exactly) */}
        <Grid size={12} sx={{ height: "100%" }}>
          <Box className="opus-scroll" sx={{ height: "calc(100% - 67px)", width: "100%", overflow: "auto", p: 2, background: "var(--surface-1)", border: "1px solid var(--border)", borderRadius: "var(--r-lg)" }}>
          {/* Scene name */}
          {(() => {
            const lockedName = isEditing || (sceneData.captions || []).some((c) => c.existingFile);
            return (
              <OpusField label="Scene Name" required
                helper={lockedName ? "Scene name cannot be changed after caption files have been saved." : "Lowercase, no spaces — used in the scene's storage path."}>
                <OpusTextInput
                  value={sceneData.scene_name || ""}
                  onChange={(v) => setSceneData((prev) => ({ ...prev, scene_name: v }))}
                  placeholder="e.g. Bluebird-MVP"
                  icon="scene"
                  disabled={lockedName}
                />
              </OpusField>
            );
          })()}

          <Box sx={{ height: 18 }} />

          {/* Organisation — locked to the route's organisation */}
          <FieldBox label="Organisation" value={orgName || "—"} />

          <Box sx={{ height: 4 }} />

          {isEditing && (
            <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Load Part Folder
              </Typography>
              <FolderSelector
                scenePath={`scenes/${orgNamePath}/${sceneNamePath}`}
                rendererId={null}
                onFolderSelect={handleFolderSelect}
                title="Select Load Part Folder"
                placeholder="Choose a folder from the scene assets..."
                selectedPath={selectedLoadPartPath}
                organizationName={orgNamePath}
                sceneName={sceneNamePath}
              />
            </Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Load Player Folder
              </Typography>
              <FolderSelector
                scenePath={`scenes/${orgNamePath}/${sceneNamePath}`}
                rendererId={null}
                onFolderSelect={handlePlayerFolderSelect}
                title="Select Load Player Folder"
                placeholder="Choose a folder from the scene assets..."
                selectedPath={selectedPlayerPath}
                organizationName={orgNamePath}
                sceneName={sceneNamePath}
              />
            </Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Load Team Badge Folder
              </Typography>
              <FolderSelector
                scenePath={`scenes/${orgNamePath}/${sceneNamePath}`}
                rendererId={null}
                onFolderSelect={handleTeamBadgeFolderSelect}
                title="Select Load Team Badge Folder"
                placeholder="Choose a folder from the scene assets..."
                selectedPath={selectedTeamBadgePath}
                organizationName={orgNamePath}
                sceneName={sceneNamePath}
              />
            </Box>
            </>
          )}

          <Box
            ref={captionsSectionRef}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            {/* OPUS-1045: surface the caption count as an information point. */}
            <Typography sx={{ fontSize: 22, fontWeight: 600, color: "#fff" }}>
              Captions
              <Typography component="span" sx={{ fontSize: 14, fontWeight: 600, color: "var(--text-dim)", ml: 1 }}>
                {captionSearch ? `${filteredCaptions.length} of ${sceneData.captions.length}` : sceneData.captions.length}
              </Typography>
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              {sceneData.captions.length > 0 && (
                <OpusSearchInput
                  value={captionSearch}
                  onChange={setCaptionSearch}
                  placeholder="Search captions…"
                  width={240}
                />
              )}
              <OpusButton variant="primary" size="sm" icon="plus" onClick={handleAddCaption}>Add Caption</OpusButton>
            </Box>
          </Box>

          {sceneData.captions.length === 0 ? (
            <Box sx={{ p: 4, border: "1px dashed var(--border-strong)", borderRadius: "var(--r-md)", textAlign: "center", color: "var(--text-dim)", fontSize: 13 }}>
              No captions yet. Add one to define a graphic for this scene.
            </Box>
          ) : filteredCaptions.length === 0 ? (
            <Box sx={{ p: 4, border: "1px dashed var(--border-strong)", borderRadius: "var(--r-md)", textAlign: "center", color: "var(--text-dim)", fontSize: 13 }}>
              No captions match "{captionSearch}".
            </Box>
          ) : (
            <CaptionsDataGrid
              captions={filteredCaptions}
              isEditing={isEditing}
              onCaptionChange={handleCaptionChange}
              onFileChange={handleFileChange}
              onRemoveCaption={handleRemoveCaption}
              onEditCaptionDetail={handleEditCaptionDetail}
              onDownloadCaption={handleDownloadCaption}
              onToggleDisabled={handleToggleCaptionDisabled}
              focusRequest={captionFocusRequest}
            />
          )}

          <input
            type="file"
            accept=".zip"
            ref={addCaptionFileInputRef}
            onChange={handleAddCaptionFileSelected}
            style={{ display: "none" }}
          />

          <OpusModal
            open={confirmCreateManuallyOpen}
            onClose={() => setConfirmCreateManuallyOpen(false)}
            title="Confirm scene name"
            icon="alertTriangle"
            width={430}
            footer={<>
              <OpusButton variant="ghost" onClick={() => setConfirmCreateManuallyOpen(false)}>Cancel</OpusButton>
              <OpusButton variant="primary" icon="check" onClick={handleConfirmCreateManually} disabled={!sceneData.scene_name}>Proceed</OpusButton>
            </>}
          >
            <p style={{ fontSize: 13.5, color: "var(--text-muted)", lineHeight: 1.55, margin: 0 }}>
              {sceneData.scene_name
                ? `Are you sure? With scene name “${sceneData.scene_name}” you will not be able to change it later once you create the caption.`
                : "Please enter a scene name first. Once you create a caption, you will not be able to change the scene name later."}
            </p>
          </OpusModal>

          <AddCaptionDialog
            open={addCaptionDialogOpen}
            onClose={() => setAddCaptionDialogOpen(false)}
            onCreateManually={handleAddCaptionManually}
            onUploadZip={handleAddCaptionUploadClick}
          />

          <OpusModal
            open={pendingNavigation !== null}
            onClose={() => setPendingNavigation(null)}
            title="Unsaved changes"
            subtitle="You have changes that haven't been saved yet. Leave anyway?"
            icon="alertTriangle"
            width={430}
            footer={<>
              <OpusButton variant="danger" onClick={handleDiscardAndLeave}>Discard Changes</OpusButton>
              <OpusButton variant="primary" icon="check" onClick={handleSaveAndLeave} disabled={uploading}>
                {uploading ? (isEditing ? "Updating…" : "Creating…") : "Save Changes"}
              </OpusButton>
            </>}
          >
            <p style={{ fontSize: 13.5, color: "var(--text-muted)", lineHeight: 1.55, margin: 0 }}>
              Discarding will lose any graphics or fields you've changed since the last Update. Saving will apply those changes before leaving.
            </p>
          </OpusModal>

          <Menu
            anchorEl={versionMenu.anchorEl}
            open={versionMenu.open}
            onClose={handleVersionMenuClose}
          >
            <Box sx={{ p: 2, minWidth: "200px" }}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Select Version</FormLabel>
                <RadioGroup
                  value={
                    versionMenu.captionIndex !== -1
                      ? sceneData.captions[versionMenu.captionIndex]?.version
                      : ""
                  }
                  onChange={(e) => handleVersionSelect(e.target.value)}
                >
                  {versionMenu.versions.map((version, idx) => (
                    <FormControlLabel
                      key={idx}
                      value={version}
                      control={<Radio />}
                      label={version}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>
          </Menu>
          </Box>
        </Grid>
      </Grid>

      {/* Loading Backdrop */}
      {uploading && (
        <Backdrop
          open={uploading}
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress color="inherit" />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Uploading Captions, please wait...
            </Typography>
          </Box>
        </Backdrop>
      )}
      {/* Versioning Backdrop Component */}
      <VersionDialog
        open={versionDialog.open}
        onClose={handleVersionDialogClose}
        caption={versionDialog.captionIndex !== -1 ? sceneData.captions[versionDialog.captionIndex] : null}
        file={versionDialog.file}
        isMajorChange={versionDialog.isMajorChange}
        onMajorChange={handleVersionChangeType}
        onConfirm={handleVersionConfirm}
      />
    </ThemeProvider>
  );
};

// The breadcrumb scene-switcher (OPUS-1045) navigates between two matches of
// this same route (scene-management/:orgId/:sceneId/edit) with just a
// different :sceneId — React Router reuses the existing SceneDetailsPage
// instance rather than remounting it, so state left over from the previous
// scene (e.g. the "preserve local additions" merge in the load effect above)
// could bleed into the newly-loaded scene. Keying on orgId+sceneId forces a
// full remount on every scene switch, matching the already-safe behavior of
// a normal navigate-away-and-back.

const SceneDetailsPageRoute = () => {
  const { orgId, sceneId } = useParams();
  return <SceneDetailsPage key={`${orgId}-${sceneId}`} />;
};

export default SceneDetailsPageRoute;
